// Helpers for normalizing Navigator responses and requests to match Paraplan

import * as CryptoJS from "crypto-js";
import type {
    Config,
    PassioUser,
    PassioRider,
    Credentials,
    PassioRouteStop,
    PassioStop,
    Place,
    Service,
    Program,
    ServiceArea,
    OnDemandUser,
    FormattedUserAndClient,
    RiderProgram,
    Access,
} from "./types";
import {
    defaultClient,
    defaultConfig,
    defaultUser,
    defaultPlace,
    defaultProgram,
    defaultParsedAddress,
    defaultAccessibility,
    defaultAutoScheduleSettings,
    defaultLocation,
} from "src/utils/defaultData";
import { omit, pick } from "lodash";
import {
    stringToJSON,
    getHexColor,
    getGeoCodedAddress,
} from "src/utils/helpers";
import {
    AutoScheduleFailAction,
    PassioService,
    AssignmentTargets,
} from "src/types";
import { colors } from "src/utils/constants";
import moment from "moment";

// todo: reevaluate hashing needs for both services
export function hashPasswordByService(
    password: string,
    service: PassioService
) {
    let hashedPass = "";
    if (service === PassioService.navigator) {
        const key = CryptoJS.lib.WordArray.random(128 / 8); // previously "djdance";
        let hash = CryptoJS.HmacSHA256(password, key);
        hashedPass = hash.toString(CryptoJS.enc.Hex).toUpperCase();
    } else {
        let hash = CryptoJS.SHA512(password);
        hashedPass = hash.toString(CryptoJS.enc.Hex).toUpperCase();
    }

    return hashedPass;
}

export function generateParaplanLoginQuery({ password, email }: Credentials) {
    return {
        email: encodeURIComponent(email),
        password: CryptoJS.SHA512(password)
            .toString(CryptoJS.enc.Hex)
            .toUpperCase(),
    };
}

export function formatPlace(
    stop: PassioStop,
    routeStop: PassioRouteStop
): Place {
    if (!stop || !routeStop) return defaultPlace;

    const formattedStop = {
        ...defaultPlace,
        name: stop.name,
        databaseId: stop.id,
        isPickUpSpot: routeStop.pickupType === 1 ? false : true,
        isDropOffSpot: routeStop.dropOffType === 1 ? false : true,
        lat: parseFloat(stop.latitude),
        lng: parseFloat(stop.longitude),
    };

    return formattedStop;
}

/*
 * Parse client detail from navigator response received as stringified object
 * @see PassioClient interface
 */

const omitRiderFields = [
    "userId",
    "address",
    "membershipId",
    "created",
    "updated",
    "archive",
    "type",
];

export const passioUserFields = [
    "id",
    "admin",
    "email",
    "fullname",
    "username",
    "address",
    "admin",
    "customer",
    "manager",
    "onDemandSettings",
    "DispatcherEmail",
    "timezone",
    "distanceUnit",
];

export const onDemandUserFields = [
    "id",
    "userId",
    "archive",
    "email",
    "accessToke",
    "archive",
    "created",
    "updated",
    "name",
    "birthdate",
    "phone",
    "accessibility",
    "address",
    "gender",
];

export function formatRiderPrograms(
    riderPrograms: RiderProgram[] = [],
    programs: Program[] = [], // formatted services
    defaultServiceId = ""
): Program[] {
    if (!riderPrograms.length) return [];

    const formattedPrograms = riderPrograms.reduce((acc: Program[], item) => {
        const baseProgram = programs.find(
            (program) => program.databaseID === item.serviceId
        );

        if (baseProgram) {
            acc.push({
                ...baseProgram,
                isDefault: baseProgram.databaseID === defaultServiceId,
                membershipID: item.membershipId,
            });
        }

        return acc;
    }, []);

    return formattedPrograms;
}

export function formatUserAndClient(
    onDemandUser: OnDemandUser,
    rider: PassioRider,
    programs: Program[] = []
): FormattedUserAndClient {
    if (!rider || !onDemandUser)
        return { user: defaultUser, client: defaultClient };

    const riderPrograms = rider.programs
        ? formatRiderPrograms(
              stringToJSON(rider.programs),
              programs,
              rider.defaultServiceId || ""
          )
        : [];

    const defaultProgramName =
        riderPrograms.find((program) => program.isDefault)?.programName || "";

    // imported riders may not have an address or accessibility
    const address = stringToJSON(onDemandUser.address) || defaultParsedAddress;
    const accessibility =
        stringToJSON(onDemandUser.accessibility) || defaultAccessibility;

    const client = {
        ...defaultClient,
        ...omit(rider, omitRiderFields),
        ...pick(onDemandUser, [
            "birthdate",
            "phone",
            "email",
            "name",
            "archive",
        ]),
        id: parseInt(onDemandUser.id),
        riderId: parseInt(rider.id),
        birthDateEpoch: moment(onDemandUser.birthdate).unix(),
        accessibility,
        status: rider.status && stringToJSON(rider.status),
        defaultProgramMembershipID: rider.membershipId || "",
        address,
        defaultServiceId: rider.defaultServiceId || "",
        defaultProgramName,
        programs: riderPrograms,
        puExtraDuration: rider.puExtraDuration
            ? parseInt(rider.puExtraDuration)
            : 0,
        doExtraDuration: rider.doExtraDuration
            ? parseInt(rider.doExtraDuration)
            : 0,
        validationCode: rider.validationCode || "",
    };

    const user = {
        ...defaultUser,
        Email: onDemandUser.email,
        ClientID: parseInt(onDemandUser.id),
        PassioUserId: onDemandUser.userId,
        UserId: parseInt(onDemandUser.userId),
        Name: onDemandUser.name,
        Key: onDemandUser.accessToken,
    };

    return { user, client };
}

export async function formatConfig(data: PassioUser): Promise<Config> {
    if (!data) return defaultConfig;
    const config = JSON.parse(data.onDemandSettings) as Config;

    const userId = parseInt(data.id);
    if (config && config?.ConnectPreferences) {
        // format agency colors and add fallbacks. Used for app themeing
        const DefaultTintString =
            getHexColor(config?.ConnectPreferences?.DefaultTintString) ||
            colors.primary;
        const SecondaryTintString =
            getHexColor(config?.ConnectPreferences?.SecondaryTintString) ||
            colors.secondary;

        const assignmentTarget =
            config?.ConnectPreferences?.AssignmentTarget ||
            AssignmentTargets.run;

        let Latitude = config?.Location?.Latitude || defaultLocation.lat;
        let Longitude = config?.Location?.Longitude || defaultLocation.lng;

        if (data.address) {
            try {
                const address = await getGeoCodedAddress(data.address);
                if (address) {
                    Latitude = address.latitude;
                    Longitude = address.longitude;
                }
            } catch (error) {
                console.error(error);
            }
        }

        return {
            ...defaultConfig,
            ...config,
            ConnectPreferences: {
                ...defaultConfig.ConnectPreferences,
                ...config.ConnectPreferences,
                DefaultTintString,
                SecondaryTintString,
                AssignmentTarget: assignmentTarget,
            },
            Location: {
                ...defaultConfig.Location,
                ...config.Location,
                Latitude,
                Longitude,
            },
            AgencyName: config.AgencyName || data.fullname,
            PassioUserId: userId,
            PassioUsername: data.username,
            PassioAgency: data.username,
            agencyId: data.id,
        };
    }

    return {
        ...defaultConfig,
        AgencyName: data.fullname,
        PassioUserId: userId,
        PassioUsername: data.username,
        PassioAgency: data.username,
        agencyId: data.id,
    };
}

function formatGeozoneIds(data: ServiceArea[]): string[] {
    if (!data) return [];

    const geozoneIds = data.reduce((accumulator: string[], area) => {
        accumulator.push(area.geofenceId);
        return accumulator;
    }, []);

    return geozoneIds;
}

export function normalizeInputString(data = "") {
    return data.toLowerCase().replace("  ", "").replace(" ", "").split(",");
}

export function formatPrograms(data: Service[]): Program[] {
    if (!data || !data.length) return [];

    const programs = data.reduce((accumulator: Program[], service) => {
        const autoScheduleSettings = {
            ...defaultAutoScheduleSettings,
            vehicleIdleValue: service.vehicleIdleValue
                ? parseInt(service.vehicleIdleValue)
                : defaultAutoScheduleSettings.vehicleIdleValue,
            vehicleIdleDuration: service.vehicleIdleDuration
                ? parseInt(service.vehicleIdleDuration)
                : defaultAutoScheduleSettings.vehicleIdleDuration,
            noPendingTripsValue: service.noPendingTripsValue
                ? parseInt(service.noPendingTripsValue)
                : defaultAutoScheduleSettings.noPendingTripsValue,
            noPendingTripsDuration: service.noPendingTripsDuration
                ? parseInt(service.noPendingTripsDuration)
                : defaultAutoScheduleSettings.noPendingTripsDuration,
            noCurrentTripValue: service.noCurrentTripValue
                ? parseInt(service.noCurrentTripValue)
                : defaultAutoScheduleSettings.noCurrentTripValue,
            currentLocationDistanceMaxValue:
                service.currentLocationDistanceMaxValue
                    ? parseInt(service.currentLocationDistanceMaxValue)
                    : defaultAutoScheduleSettings.currentLocationDistanceMaxValue,
            nextLocationDistanceMaxValue: service.nextLocationDistanceMaxValue
                ? parseInt(service.nextLocationDistanceMaxValue)
                : defaultAutoScheduleSettings.nextLocationDistanceMaxValue,
            distanceUnitLengthValue: service.distanceUnitLengthValue
                ? parseInt(service.distanceUnitLengthValue)
                : defaultAutoScheduleSettings.distanceUnitLengthValue,
        };

        const program = {
            ...defaultProgram,
            databaseID: service.id,
            programName: service.name,
            pickUpLOS: service.pickUpLosId,
            dropOffLOS: service.dropOffLosId,
            activeGeozoneIds: formatGeozoneIds(service.serviceArea),
            serviceCalendarId: service.serviceCalendarId,
            puExtraDuration: service.puExtraDuration
                ? parseInt(service.puExtraDuration)
                : 0,
            doExtraDuration: service.doExtraDuration
                ? parseInt(service.doExtraDuration)
                : 0,
            autoScheduleFailAction: service.autoscheduleFailAction
                ? parseInt(service.autoscheduleFailAction)
                : AutoScheduleFailAction.NoAction,
            riderCanScheduleFutureTrip: service.riderCanScheduleFutureTrip || 0,
            autoScheduleSettings,
            onDemandOffset: service.onDemandOffset
                ? +service.onDemandOffset
                : 15, // default to 15 minutes
            membershipIsRequired: service.membershipRequired ? true : false,
            validDomains: service.validDomains
                ? normalizeInputString(service.validDomains)
                : "",
            validEmails: service.validEmails
                ? normalizeInputString(service.validEmails)
                : "",
            ...pick(service, ["id", "validationCode", "validationRequired"]),
        };

        accumulator.push(program);
        return accumulator;
    }, []);

    return programs;
}

export function getCustomerIdsFromAccess(access: Access[] = []) {
    const ids = access.reduce((acc: string[], { accountId }) => {
        if (accountId && !acc.includes(accountId)) {
            acc.push(accountId);
        }
        return acc;
    }, []);
    return ids;
}

export const generateRandomSixDigitNumber = () =>
    Math.floor(100000 + Math.random() * 900000);
